import type { ChatRole } from "./types";

export interface Starter {
  label: string;
  prompt: string;
}

function firstNameOf(name: string): string {
  const n = (name ?? "").trim().split(/\s+/)[0];
  return n && n.toLowerCase() !== "there" && n.toLowerCase() !== "friend" ? n : "";
}

export function getStarters(role: ChatRole, grade: string | null, _name: string): Starter[] {
  const g = grade ?? "4";
  switch (role) {
    case "guest":
      return [
        { label: "Build me a mini study plan", prompt: `I have about 15 minutes a day — build me a 3-day plan for Grade ${g} math.` },
        { label: "Quick practice test", prompt: `Give me a short Grade ${g} math practice test right now.` },
        { label: "Mix math and English", prompt: `Quiz me on Grade ${g} math and English together — 10 questions mixed.` },
        { label: "Try a learning game", prompt: `Show me a Grade ${g} learning game I can play right now.` },
      ];
    case "student":
      return [
        { label: "What should I work on this week?", prompt: "Look at my recent progress and build me a plan for this week." },
        { label: "How am I doing?", prompt: "Show me my progress and where I should focus next." },
        { label: `Mixed quiz — my weakest areas`, prompt: `Give me a mixed 10-question quiz focused on my weakest subjects.` },
        { label: "Prep me for a test on Friday", prompt: "I have a test on Friday — build me a short prep plan." },
      ];
    case "parent":
      return [
        { label: "Plan our week", prompt: "Build me a family plan for this week for each of my children based on their progress." },
        { label: "How are my children doing?", prompt: "Give me a thoughtful summary of my children's recent progress." },
        { label: "Upcoming sessions", prompt: "What tutoring sessions are coming up this week?" },
        { label: "A gentle nightly routine", prompt: "Suggest a realistic 15-minute weeknight practice routine for my children." },
      ];
    case "instructor":
      return [
        { label: "Draft tomorrow's session", prompt: "Draft tomorrow's tutoring session plan with objectives and materials." },
        { label: "Today's schedule", prompt: "Walk me through today's tutoring sessions." },
        { label: "Diagnose a student", prompt: "Pick one of my students and suggest a diagnostic plan for their next session." },
        { label: "My tests", prompt: "List my active practice tests." },
      ];
    case "admin":
      return [
        { label: "Open admin console", prompt: "Take me to the admin dashboard." },
      ];
  }
}

export function greeting(role: ChatRole, name: string): string {
  const first = firstNameOf(name);
  const hi = first ? `Hi ${first}` : "Hi";
  switch (role) {
    case "guest":
      return "Hey — I'm Ada, your learning coach. Tell me what you're working on and I'll build a short plan or jump straight into a practice round. No sign-in needed to start.";
    case "student":
      return `${hi} — good to see you. Want to pick up where you left off, or should I put together a short plan for this week?`;
    case "parent":
      return `${hi}. I can pull together a thoughtful week for your children, walk through how they're doing, or help you plan a gentle nightly routine. Where should we start?`;
    case "instructor":
      return `${hi}. Want me to draft tomorrow's session, review today's schedule, or diagnose a student's next steps?`;
    case "admin":
      return `${hi}. Chat is read-only for admins — open /admin for full controls.`;
  }
}
